import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BookOpen, BarChart3, Scale, TrendingUp, ArrowLeftRight,
  Layers, Download, Printer, RefreshCw, Search,
  ChevronUp, ChevronDown, ChevronRight, Building2, Calendar
} from 'lucide-react';
import apiClient from '../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const REPORT_TYPES = [
  { key: 'daybook',      label: 'Day Book',              icon: BookOpen,      color: '#0f766e' },
  { key: 'ledger',       label: 'Account Ledger',        icon: BarChart3,     color: '#1d4ed8' },
  { key: 'trial_balance',label: 'Trial Balance',         icon: Scale,         color: '#7c3aed' },
  { key: 'balance_sheet',label: 'Balance Sheet',         icon: Layers,        color: '#b45309' },
  { key: 'profit_loss',  label: 'Profit & Loss',         icon: TrendingUp,    color: '#be123c' },
  { key: 'trading_pl',   label: 'Trading & P&L',         icon: ArrowLeftRight,color: '#0369a1' },
];

const VOUCHER_COLORS = {
  'CV': { bg: '#fef3c7', color: '#92400e', label: 'Cash Payment' },
  'CR': { bg: '#dcfce7', color: '#14532d', label: 'Cash Receipt' },
  'BV': { bg: '#dbeafe', color: '#1e3a8a', label: 'Bank Payment' },
  'BR': { bg: '#ede9fe', color: '#4c1d95', label: 'Bank Receipt' },
  'JV': { bg: '#f1f5f9', color: '#334155', label: 'Journal'      },
};

const ACTYPE_ORDER = { Asset: 1, Liability: 2, Equity: 3, Income: 4, Expense: 5 };

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const fmtDate = v => {
  if (!v) return '—';
  const d = new Date(v + 'T00:00:00');
  return isNaN(d) ? v : `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
};
const fmtNum = (v, decimals = 2) => {
  const n = parseFloat(v);
  if (isNaN(n) || n === 0) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};
const fmtNumRaw = (v, decimals = 2) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};
const abs = v => Math.abs(parseFloat(v) || 0);

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────
const AmtCell = ({ value, color, bold }) => {
  const n = parseFloat(value) || 0;
  if (n === 0) return <span style={{ color: '#94a3b8' }}>—</span>;
  return (
    <span style={{
      fontFamily: "'Courier Prime', 'Courier New', monospace",
      color: color || (n < 0 ? '#dc2626' : '#1e293b'),
      fontWeight: bold ? '700' : '600',
    }}>
      {fmtNumRaw(Math.abs(n))}
    </span>
  );
};

const SectionHeader = ({ title, amount, amtLabel, color }) => (
  <tr>
    <td colSpan={99} style={{
      padding: '10px 14px 6px',
      background: `linear-gradient(90deg, ${color}18 0%, transparent 60%)`,
      borderLeft: `3px solid ${color}`,
      borderBottom: `1px solid ${color}40`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: '0.88rem', fontWeight: '700', color, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
          {title}
        </span>
        {amount != null && (
          <span style={{ fontFamily: "'Courier Prime','Courier New',monospace", fontSize: '0.85rem', fontWeight: '700', color }}>
            {amtLabel && <span style={{ fontSize: '0.72rem', marginRight: 6, opacity: 0.7 }}>{amtLabel}</span>}
            {fmtNumRaw(amount)}
          </span>
        )}
      </div>
    </td>
  </tr>
);

const TotalRow = ({ label, value, color, shade }) => (
  <tr style={{ background: shade || '#f8fafc' }}>
    <td colSpan={99} style={{ padding: '8px 14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: '700', color: color || '#1e293b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </span>
        <span style={{
          fontFamily: "'Courier Prime','Courier New',monospace",
          fontSize: '0.95rem', fontWeight: '800',
          color: color || '#1e293b',
          borderTop: `2px solid ${color || '#94a3b8'}`,
          borderBottom: `3px double ${color || '#94a3b8'}`,
          padding: '2px 8px',
        }}>
          {fmtNumRaw(value)}
        </span>
      </div>
    </td>
  </tr>
);

// ─────────────────────────────────────────────────────────────────────────────
// Report renderers
// ─────────────────────────────────────────────────────────────────────────────

// DAYBOOK
const DaybookTable = ({ rows, searchTerm }) => {
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  let filtered = rows;
  if (searchTerm.trim()) {
    const q = searchTerm.toLowerCase();
    filtered = filtered.filter(r => Object.values(r).some(v => String(v ?? '').toLowerCase().includes(q)));
  }
  if (sortKey) {
    filtered = [...filtered].sort((a, b) => {
      const av = a[sortKey] ?? '', bv = b[sortKey] ?? '';
      const an = parseFloat(av), bn = parseFloat(bv);
      const cmp = !isNaN(an) && !isNaN(bn) ? an - bn : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }

  const Th = ({ k, label, align }) => (
    <th onClick={() => { setSortKey(k); setSortDir(s => sortKey === k && s === 'asc' ? 'desc' : 'asc'); }}
      style={{ ...thStyle, textAlign: align || 'left', cursor: 'pointer', userSelect: 'none' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
        {label}
        {sortKey === k ? (sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />) : <ChevronUp size={11} style={{ opacity: 0.3 }} />}
      </span>
    </th>
  );

  // Group by date + voucher for visual grouping
  let lastVoucher = null;
  const totalDr = filtered.reduce((s, r) => s + (parseFloat(r.DebitAmount) || 0), 0);
  const totalCr = filtered.reduce((s, r) => s + (parseFloat(r.CreditAmount) || 0), 0);

  return (
    <div style={{ overflow: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr style={{ background: '#1e293b' }}>
            {[['VoucherDate','Date',90],['VoucherNo','Voucher',120],['VoucherTypeName','Type',130],
              ['AccountCode','Code',80],['AccountName','Account',180],['GroupName','Group',130],
              ['DebitAmount','Debit',120,true],['CreditAmount','Credit',120,true],['Narration','Narration',200]
            ].map(([k,label,w,right]) => (
              <Th key={k} k={k} label={label} align={right ? 'right' : 'left'} />
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((r, i) => {
            const isNewVoucher = r.JournalID !== lastVoucher;
            lastVoucher = r.JournalID;
            const vc = VOUCHER_COLORS[r.VoucherTypeCode] || VOUCHER_COLORS.JV;
            return (
              <tr key={r.DetailID || i} style={{
                background: isNewVoucher && i > 0 ? '#f8fafc' : (i % 2 === 0 ? '#fff' : '#fafafa'),
                borderTop: isNewVoucher && i > 0 ? '1px solid #e2e8f0' : undefined,
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                onMouseLeave={e => e.currentTarget.style.background = isNewVoucher && i > 0 ? '#f8fafc' : (i % 2 === 0 ? '#fff' : '#fafafa')}
              >
                <td style={tdStyle}><span style={{ fontSize: '0.78rem', color: '#475569' }}>{fmtDate(r.VoucherDate)}</span></td>
                <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#0f766e', fontWeight: '600' }}>{r.VoucherNo}</span></td>
                <td style={tdStyle}>
                  <span style={{ background: vc.bg, color: vc.color, padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: '700' }}>
                    {r.VoucherTypeName}
                  </span>
                </td>
                <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#64748b' }}>{r.AccountCode}</span></td>
                <td style={{ ...tdStyle, maxWidth: 180 }}>{r.AccountName}</td>
                <td style={{ ...tdStyle, color: '#64748b', fontSize: '0.78rem' }}>{r.GroupName}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <AmtCell value={r.DebitAmount} color="#dc2626" />
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <AmtCell value={r.CreditAmount} color="#16a34a" />
                </td>
                <td style={{ ...tdStyle, color: '#64748b', fontSize: '0.78rem', maxWidth: 200 }}>{r.Narration || '—'}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ background: '#1e293b' }}>
            <td colSpan={6} style={{ ...tdStyle, color: '#94a3b8', fontSize: '0.78rem', padding: '8px 14px' }}>
              TOTAL — {filtered.length} entries
            </td>
            <td style={{ ...tdStyle, textAlign: 'right', color: '#fca5a5', fontFamily: 'monospace', fontWeight: '800', fontSize: '0.85rem' }}>
              {fmtNumRaw(totalDr)}
            </td>
            <td style={{ ...tdStyle, textAlign: 'right', color: '#86efac', fontFamily: 'monospace', fontWeight: '800', fontSize: '0.85rem' }}>
              {fmtNumRaw(totalCr)}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// LEDGER
const LedgerTable = ({ rows, searchTerm }) => {
  // Group by account head
  const accounts = {};
  rows.forEach(r => {
    const key = r.AcHeadID || r.AccountCode;
    if (!accounts[key]) {
      accounts[key] = {
        code: r.AccountCode, name: r.AccountName,
        group: r.GroupName, type: r.AccountType,
        openingBalance: parseFloat(r.OpeningBalance) || 0,
        lines: [],
      };
    }
    if (r.TxnDate) accounts[key].lines.push(r);
  });

  return (
    <div>
      {Object.entries(accounts).map(([key, acct]) => {
        let runBal = acct.openingBalance;
        const lines = acct.lines.sort((a,b) => {
          if (a.TxnDate < b.TxnDate) return -1;
          if (a.TxnDate > b.TxnDate) return 1;
          return (a.SortKey||0) - (b.SortKey||0);
        });

        return (
          <div key={key} style={{ marginBottom: 28 }}>
            {/* Account header */}
            <div style={{
              background: 'linear-gradient(90deg,#1e293b,#334155)',
              padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              borderRadius: '6px 6px 0 0',
            }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'baseline' }}>
                <span style={{ fontFamily: 'monospace', color: '#94a3b8', fontSize: '0.78rem' }}>{acct.code}</span>
                <span style={{ fontFamily: "'DM Serif Display',Georgia,serif", color: '#f1f5f9', fontSize: '1rem', fontWeight: '700' }}>{acct.name}</span>
                <span style={{ background: '#0f172a', color: '#64748b', padding: '1px 8px', borderRadius: 10, fontSize: '0.72rem' }}>{acct.group}</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Opening Balance</div>
                <div style={{ fontFamily: 'monospace', color: acct.openingBalance >= 0 ? '#fca5a5' : '#86efac', fontWeight: '700', fontSize: '0.9rem' }}>
                  {fmtNumRaw(Math.abs(acct.openingBalance))} {acct.openingBalance >= 0 ? 'Dr' : 'Cr'}
                </div>
              </div>
            </div>
            <table style={{ ...tableStyle, borderRadius: '0 0 6px 6px' }}>
              <thead>
                <tr style={{ background: '#334155' }}>
                  {['Date','Voucher No','Type','Debit','Credit','Balance','Narration'].map((h,i) => (
                    <th key={i} style={{ ...thStyle, textAlign: i >= 3 ? 'right' : 'left', background: 'transparent' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 && (
                  <tr><td colSpan={7} style={{ ...tdStyle, color: '#94a3b8', textAlign: 'center', padding: 20 }}>No transactions in this period</td></tr>
                )}
                {lines.map((r, i) => {
                  const dr = parseFloat(r.DebitAmount) || 0;
                  const cr = parseFloat(r.CreditAmount) || 0;
                  runBal = runBal + dr - cr;
                  return (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#f8fafc'}
                    >
                      <td style={tdStyle}><span style={{ fontSize: '0.78rem', color: '#475569' }}>{fmtDate(r.TxnDate)}</span></td>
                      <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#0f766e', fontWeight: '600' }}>{r.VoucherNo}</span></td>
                      <td style={tdStyle}><span style={{ fontSize: '0.78rem', color: '#64748b' }}>{r.VoucherType}</span></td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={dr} color="#dc2626" /></td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={cr} color="#16a34a" /></td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <span style={{
                          fontFamily: "'Courier Prime','Courier New',monospace",
                          fontWeight: '700',
                          color: runBal >= 0 ? '#dc2626' : '#16a34a',
                        }}>
                          {fmtNumRaw(Math.abs(runBal))} {runBal >= 0 ? 'Dr' : 'Cr'}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, color: '#64748b', fontSize: '0.78rem', maxWidth: 200 }}>{r.Narration || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
              {lines.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#f1f5f9' }}>
                    <td colSpan={3} style={{ ...tdStyle, fontWeight: '700', fontSize: '0.78rem', color: '#334155' }}>CLOSING BALANCE</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', fontWeight: '700', color: '#dc2626' }}>
                      {fmtNumRaw(lines.reduce((s,r) => s + (parseFloat(r.DebitAmount)||0), 0))}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', fontWeight: '700', color: '#16a34a' }}>
                      {fmtNumRaw(lines.reduce((s,r) => s + (parseFloat(r.CreditAmount)||0), 0))}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <span style={{
                        fontFamily: "'Courier Prime','Courier New',monospace", fontWeight: '800', fontSize: '0.9rem',
                        color: runBal >= 0 ? '#dc2626' : '#16a34a',
                        borderTop: '2px solid', borderBottom: '3px double', padding: '2px 8px',
                      }}>
                        {fmtNumRaw(Math.abs(runBal))} {runBal >= 0 ? 'Dr' : 'Cr'}
                      </span>
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        );
      })}
    </div>
  );
};

// TRIAL BALANCE
const TrialBalanceTable = ({ rows }) => {
  let totOB = 0, totDr = 0, totCr = 0;
  const computed = rows.map(r => {
    const ob = parseFloat(r.OpeningBalance) || 0;
    const dr = parseFloat(r.PeriodDebit) || 0;
    const cr = parseFloat(r.PeriodCredit) || 0;
    const closing = ob + dr - cr;
    totOB += ob; totDr += dr; totCr += cr;
    return { ...r, _ob: ob, _dr: dr, _cr: cr, _closing: closing };
  });

  // Group by AccountType
  const groups = {};
  computed.forEach(r => {
    const g = r.GroupName || r.AccountType;
    if (!groups[g]) groups[g] = { type: r.AccountType, rows: [] };
    groups[g].rows.push(r);
  });

  return (
    <div style={{ overflow: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr style={{ background: '#1e293b' }}>
            <th style={{ ...thStyle, width: 80 }}>Code</th>
            <th style={{ ...thStyle }}>Account Name</th>
            <th style={{ ...thStyle, width: 100 }}>Group</th>
            <th style={{ ...thStyle, textAlign: 'right', width: 130 }}>Opening Bal</th>
            <th style={{ ...thStyle, textAlign: 'right', width: 130 }}>Period Debit</th>
            <th style={{ ...thStyle, textAlign: 'right', width: 130 }}>Period Credit</th>
            <th style={{ ...thStyle, textAlign: 'right', width: 140 }}>Closing Balance</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(groups).map(([gname, gdata]) => (
            <React.Fragment key={gname}>
              <tr style={{ background: '#f1f5f9' }}>
                <td colSpan={7} style={{ ...tdStyle, fontWeight: '700', fontSize: '0.78rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '6px 14px' }}>
                  {gname}
                </td>
              </tr>
              {gdata.rows.map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'}
                >
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.78rem', color: '#64748b' }}>{r.AccountCode}</td>
                  <td style={tdStyle}>{r.AccountName}</td>
                  <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#64748b' }}>{r.GroupName}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    {r._ob !== 0 && <span style={{ fontFamily: 'monospace', color: r._ob >= 0 ? '#dc2626' : '#16a34a', fontWeight: '600', fontSize: '0.82rem' }}>
                      {fmtNumRaw(Math.abs(r._ob))} {r._ob >= 0 ? 'Dr' : 'Cr'}
                    </span>}
                    {r._ob === 0 && <span style={{ color: '#cbd5e1' }}>—</span>}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r._dr} color="#dc2626" /></td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r._cr} color="#16a34a" /></td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <span style={{
                      fontFamily: 'monospace', fontWeight: '700', fontSize: '0.82rem',
                      color: r._closing >= 0 ? '#dc2626' : '#16a34a',
                    }}>
                      {fmtNumRaw(Math.abs(r._closing))} {r._closing >= 0 ? 'Dr' : 'Cr'}
                    </span>
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: '#0f172a' }}>
            <td colSpan={3} style={{ ...tdStyle, color: '#94a3b8', fontWeight: '700', fontSize: '0.78rem', padding: '10px 14px' }}>GRAND TOTAL</td>
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#fbbf24', fontWeight: '800', fontSize: '0.85rem' }}>
              {fmtNumRaw(Math.abs(totOB))} {totOB >= 0 ? 'Dr' : 'Cr'}
            </td>
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#fca5a5', fontWeight: '800', fontSize: '0.85rem' }}>{fmtNumRaw(totDr)}</td>
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#86efac', fontWeight: '800', fontSize: '0.85rem' }}>{fmtNumRaw(totCr)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// BALANCE SHEET
const BalanceSheetTable = ({ rows, summary }) => {
  const assets = rows.filter(r => r.AccountType === 'Asset');
  const liabEquity = rows.filter(r => r.AccountType !== 'Asset');

  const grouped = items => {
    const g = {};
    items.forEach(r => {
      if (!g[r.GroupName]) g[r.GroupName] = { class: r.AccountClass, rows: [] };
      g[r.GroupName].rows.push(r);
    });
    return g;
  };

  const AssetSide = () => {
    const g = grouped(assets);
    return (
      <>
        <SectionHeader title="ASSETS" color="#0f766e" />
        {Object.entries(g).map(([gname, gdata]) => (
          <React.Fragment key={gname}>
            <tr style={{ background: '#f0fdf4' }}>
              <td colSpan={3} style={{ ...tdStyle, fontWeight: '700', fontSize: '0.78rem', color: '#166534', padding: '5px 14px 5px 24px', letterSpacing: '0.04em' }}>{gname}</td>
            </tr>
            {gdata.rows.map((r, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f7fffe' }}
                onMouseEnter={e => e.currentTarget.style.background = '#ecfdf5'}
                onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#f7fffe'}
              >
                <td style={{ ...tdStyle, paddingLeft: 32, fontFamily: 'monospace', fontSize: '0.78rem', color: '#64748b', width: 90 }}>{r.AccountCode}</td>
                <td style={tdStyle}>{r.AccountName}</td>
                <td style={{ ...tdStyle, textAlign: 'right', width: 140 }}>
                  <span style={{ fontFamily: 'monospace', fontWeight: '700', color: '#0f766e', fontSize: '0.85rem' }}>
                    {fmtNumRaw(Math.abs(parseFloat(r.ClosingBalance) || 0))}
                  </span>
                </td>
              </tr>
            ))}
            <tr style={{ background: '#f0fdf4' }}>
              <td colSpan={2} style={{ ...tdStyle, textAlign: 'right', fontSize: '0.78rem', color: '#16a34a', fontWeight: '600' }}>
                Total {gname}
              </td>
              <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', fontWeight: '700', color: '#166534', borderTop: '1px solid #86efac' }}>
                {fmtNumRaw(gdata.rows.reduce((s,r) => s + (parseFloat(r.ClosingBalance)||0), 0))}
              </td>
            </tr>
          </React.Fragment>
        ))}
        <TotalRow label="Total Assets" value={summary.total_assets} color="#0f766e" shade="#dcfce7" />
      </>
    );
  };

  const LiabSide = () => {
    const g = grouped(liabEquity);
    return (
      <>
        <SectionHeader title="LIABILITIES & EQUITY" color="#b45309" />
        {Object.entries(g).map(([gname, gdata]) => (
          <React.Fragment key={gname}>
            <tr style={{ background: '#fffbeb' }}>
              <td colSpan={3} style={{ ...tdStyle, fontWeight: '700', fontSize: '0.78rem', color: '#92400e', padding: '5px 14px 5px 24px', letterSpacing: '0.04em' }}>{gname}</td>
            </tr>
            {gdata.rows.map((r, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fffdf5' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fef9ec'}
                onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fffdf5'}
              >
                <td style={{ ...tdStyle, paddingLeft: 32, fontFamily: 'monospace', fontSize: '0.78rem', color: '#64748b', width: 90 }}>{r.AccountCode}</td>
                <td style={tdStyle}>{r.AccountName}</td>
                <td style={{ ...tdStyle, textAlign: 'right', width: 140 }}>
                  <span style={{ fontFamily: 'monospace', fontWeight: '700', color: '#b45309', fontSize: '0.85rem' }}>
                    {fmtNumRaw(Math.abs(parseFloat(r.ClosingBalance) || 0))}
                  </span>
                </td>
              </tr>
            ))}
            <tr style={{ background: '#fffbeb' }}>
              <td colSpan={2} style={{ ...tdStyle, textAlign: 'right', fontSize: '0.78rem', color: '#b45309', fontWeight: '600' }}>
                Total {gname}
              </td>
              <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', fontWeight: '700', color: '#92400e', borderTop: '1px solid #fcd34d' }}>
                {fmtNumRaw(Math.abs(gdata.rows.reduce((s,r) => s + (parseFloat(r.ClosingBalance)||0), 0)))}
              </td>
            </tr>
          </React.Fragment>
        ))}
        <TotalRow label="Total Liabilities & Equity" value={summary.total_liabilities} color="#b45309" shade="#fef3c7" />
      </>
    );
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      <div style={{ border: '1px solid #d1fae5', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ ...tableStyle, margin: 0 }}><tbody><AssetSide /></tbody></table>
      </div>
      <div style={{ border: '1px solid #fde68a', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ ...tableStyle, margin: 0 }}><tbody><LiabSide /></tbody></table>
      </div>
      {Math.abs(summary.difference || 0) > 0.01 && (
        <div style={{ gridColumn: '1/-1', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 16px', color: '#dc2626', fontSize: '0.85rem' }}>
          ⚠ Balance sheet difference: {fmtNumRaw(summary.difference)} — check opening balances or journal entries.
        </div>
      )}
    </div>
  );
};

// PROFIT & LOSS
const ProfitLossTable = ({ rows, summary }) => {
  const income  = rows.filter(r => r.AccountType === 'Income');
  const expense = rows.filter(r => r.AccountType === 'Expense');

  const renderGroup = (items, isIncome) => {
    const g = {};
    items.forEach(r => {
      if (!g[r.GroupName]) g[r.GroupName] = [];
      g[r.GroupName].push(r);
    });
    const color = isIncome ? '#166534' : '#991b1b';
    return Object.entries(g).map(([gname, gRows]) => {
      const total = gRows.reduce((s,r) => {
        const mv = parseFloat(r.PeriodMovement) || 0;
        return s + (isIncome ? -mv : mv);
      }, 0);
      return (
        <React.Fragment key={gname}>
          <tr style={{ background: isIncome ? '#f0fdf4' : '#fef2f2' }}>
            <td colSpan={3} style={{ ...tdStyle, fontWeight: '700', fontSize: '0.78rem', color, padding: '5px 14px 5px 24px' }}>{gname}</td>
          </tr>
          {gRows.map((r, i) => {
            const val = isIncome ? -(parseFloat(r.PeriodMovement)||0) : (parseFloat(r.PeriodMovement)||0);
            return (
              <tr key={i} style={{ background: i%2===0?'#fff': isIncome?'#f7fffe':'#fff5f5' }}
                onMouseEnter={e=>e.currentTarget.style.background=isIncome?'#ecfdf5':'#fee2e2'}
                onMouseLeave={e=>e.currentTarget.style.background=i%2===0?'#fff':isIncome?'#f7fffe':'#fff5f5'}
              >
                <td style={{ ...tdStyle, fontFamily:'monospace', fontSize:'0.78rem', color:'#64748b', paddingLeft:32, width:90 }}>{r.AccountCode}</td>
                <td style={tdStyle}>{r.AccountName}</td>
                <td style={{ ...tdStyle, textAlign:'right', width:140 }}>
                  <span style={{ fontFamily:'monospace', fontWeight:'700', color, fontSize:'0.85rem' }}>
                    {fmtNumRaw(val)}
                  </span>
                </td>
              </tr>
            );
          })}
          <tr style={{ background: isIncome ? '#dcfce7' : '#fee2e2' }}>
            <td colSpan={2} style={{ ...tdStyle, textAlign:'right', fontSize:'0.78rem', color, fontWeight:'600' }}>Total {gname}</td>
            <td style={{ ...tdStyle, textAlign:'right', fontFamily:'monospace', fontWeight:'700', color, borderTop:`1px solid ${isIncome?'#86efac':'#fca5a5'}` }}>
              {fmtNumRaw(total)}
            </td>
          </tr>
        </React.Fragment>
      );
    });
  };

  const netColor = summary.net_profit >= 0 ? '#166534' : '#991b1b';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      <div style={{ border: '1px solid #d1fae5', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ ...tableStyle, margin: 0 }}>
          <tbody>
            <SectionHeader title="INCOME" color="#166534" />
            {renderGroup(income, true)}
            <TotalRow label="Total Income" value={summary.total_income} color="#166534" shade="#dcfce7" />
          </tbody>
        </table>
      </div>
      <div style={{ border: '1px solid #fecaca', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ ...tableStyle, margin: 0 }}>
          <tbody>
            <SectionHeader title="EXPENSES" color="#991b1b" />
            {renderGroup(expense, false)}
            <TotalRow label="Total Expenses" value={summary.total_expense} color="#991b1b" shade="#fee2e2" />
          </tbody>
        </table>
      </div>
      <div style={{ gridColumn:'1/-1', border:`2px solid ${netColor}`, borderRadius:8, padding:'14px 20px', background: summary.net_profit>=0?'#f0fdf4':'#fef2f2', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ fontFamily:"'DM Serif Display',Georgia,serif", fontSize:'1.1rem', fontWeight:'700', color:netColor }}>
          {summary.net_profit >= 0 ? '✦ Net Profit' : '✦ Net Loss'}
        </span>
        <span style={{ fontFamily:"'Courier Prime','Courier New',monospace", fontSize:'1.3rem', fontWeight:'800', color:netColor, borderTop:`3px double ${netColor}`, borderBottom:`3px double ${netColor}`, padding:'4px 12px' }}>
          {fmtNumRaw(Math.abs(summary.net_profit))}
        </span>
      </div>
    </div>
  );
};

// TRADING & P&L
const TradingPLTable = ({ rows, summary }) => {
  const TRADING_INCOME_CLASS   = 'Operating Income';
  const TRADING_EXPENSE_CLASS  = 'Cost of Sales';

  const tradingIncome  = rows.filter(r => r.AccountType==='Income'  && r.AccountClass===TRADING_INCOME_CLASS);
  const tradingExpense = rows.filter(r => r.AccountType==='Expense' && r.AccountClass===TRADING_EXPENSE_CLASS);
  const plIncome       = rows.filter(r => r.AccountType==='Income'  && r.AccountClass!==TRADING_INCOME_CLASS);
  const plExpense      = rows.filter(r => r.AccountType==='Expense' && r.AccountClass!==TRADING_EXPENSE_CLASS);

  const grossProfit = summary.gross_profit ?? (summary.total_income - summary.total_expense);
  const netProfit   = summary.net_profit;

  const renderItems = (items, isIncome, color) => items.map((r, i) => {
    const val = isIncome ? -(parseFloat(r.PeriodMovement)||0) : (parseFloat(r.PeriodMovement)||0);
    return (
      <tr key={i} style={{ background: i%2===0?'#fff':'#fafafa' }}
        onMouseEnter={e=>e.currentTarget.style.background='#eff6ff'}
        onMouseLeave={e=>e.currentTarget.style.background=i%2===0?'#fff':'#fafafa'}
      >
        <td style={{ ...tdStyle, fontFamily:'monospace', fontSize:'0.78rem', color:'#64748b', paddingLeft:28, width:90 }}>{r.AccountCode}</td>
        <td style={tdStyle}>{r.AccountName}</td>
        <td style={{ ...tdStyle, textAlign:'right', width:140 }}>
          <span style={{ fontFamily:'monospace', fontWeight:'700', color, fontSize:'0.85rem' }}>{fmtNumRaw(val)}</span>
        </td>
      </tr>
    );
  });

  const sum = (items, isIncome) => items.reduce((s,r) => {
    const mv = parseFloat(r.PeriodMovement)||0;
    return s + (isIncome ? -mv : mv);
  }, 0);

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

      {/* LEFT COLUMN */}
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

        {/* Trading - Sales */}
        <div style={{ border:'1px solid #d1fae5', borderRadius:8, overflow:'hidden' }}>
          <table style={{ ...tableStyle, margin:0 }}>
            <tbody>
              <SectionHeader title="TRADING A/C — SALES" color="#0f766e" />
              {tradingIncome.length === 0 && <tr><td colSpan={3} style={{ ...tdStyle, color:'#94a3b8', padding:12, textAlign:'center' }}>No trading income entries</td></tr>}
              {renderItems(tradingIncome, true, '#0f766e')}
              <TotalRow label="Gross Sales" value={sum(tradingIncome, true)} color="#0f766e" shade="#dcfce7" />
            </tbody>
          </table>
        </div>

        {/* P&L - Other Income */}
        <div style={{ border:'1px solid #dbeafe', borderRadius:8, overflow:'hidden' }}>
          <table style={{ ...tableStyle, margin:0 }}>
            <tbody>
              <SectionHeader title="P & L A/C — OTHER INCOME" color="#1d4ed8" />
              {plIncome.length === 0 && <tr><td colSpan={3} style={{ ...tdStyle, color:'#94a3b8', padding:12, textAlign:'center' }}>No other income entries</td></tr>}
              {renderItems(plIncome, true, '#1d4ed8')}
              {plIncome.length > 0 && <TotalRow label="Total Other Income" value={sum(plIncome, true)} color="#1d4ed8" shade="#dbeafe" />}
            </tbody>
          </table>
        </div>

        {/* Gross Profit b/d */}
        <div style={{ border:`2px solid ${grossProfit>=0?'#0f766e':'#dc2626'}`, borderRadius:8, padding:'12px 16px', background:grossProfit>=0?'#f0fdf4':'#fef2f2', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontFamily:"'DM Serif Display',Georgia,serif", fontSize:'0.95rem', fontWeight:'700', color:grossProfit>=0?'#166534':'#991b1b' }}>
            {grossProfit>=0 ? 'Gross Profit b/d' : 'Gross Loss b/d'}
          </span>
          <span style={{ fontFamily:"monospace", fontSize:'1rem', fontWeight:'800', color:grossProfit>=0?'#166534':'#991b1b' }}>
            {fmtNumRaw(Math.abs(grossProfit))}
          </span>
        </div>

      </div>

      {/* RIGHT COLUMN */}
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

        {/* Trading - Cost of Sales */}
        <div style={{ border:'1px solid #fecaca', borderRadius:8, overflow:'hidden' }}>
          <table style={{ ...tableStyle, margin:0 }}>
            <tbody>
              <SectionHeader title="TRADING A/C — COST OF SALES" color="#dc2626" />
              {tradingExpense.length === 0 && <tr><td colSpan={3} style={{ ...tdStyle, color:'#94a3b8', padding:12, textAlign:'center' }}>No cost of sales entries</td></tr>}
              {renderItems(tradingExpense, false, '#dc2626')}
              <TotalRow label="Total Cost of Sales" value={sum(tradingExpense, false)} color="#dc2626" shade="#fee2e2" />
            </tbody>
          </table>
        </div>

        {/* P&L - Operating Expenses */}
        <div style={{ border:'1px solid #fde8d8', borderRadius:8, overflow:'hidden' }}>
          <table style={{ ...tableStyle, margin:0 }}>
            <tbody>
              <SectionHeader title="P & L A/C — OPERATING EXPENSES" color="#c2410c" />
              {plExpense.length === 0 && <tr><td colSpan={3} style={{ ...tdStyle, color:'#94a3b8', padding:12, textAlign:'center' }}>No operating expenses</td></tr>}
              {renderItems(plExpense, false, '#c2410c')}
              {plExpense.length > 0 && <TotalRow label="Total Operating Expenses" value={sum(plExpense, false)} color="#c2410c" shade="#fce8d5" />}
            </tbody>
          </table>
        </div>

        {/* Net Profit/Loss */}
        <div style={{ border:`2px solid ${netProfit>=0?'#166534':'#dc2626'}`, borderRadius:8, padding:'12px 16px', background:netProfit>=0?'#f0fdf4':'#fef2f2', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontFamily:"'DM Serif Display',Georgia,serif", fontSize:'0.95rem', fontWeight:'700', color:netProfit>=0?'#166534':'#991b1b' }}>
            {netProfit>=0 ? '✦ Net Profit' : '✦ Net Loss'}
          </span>
          <span style={{ fontFamily:"'Courier Prime','Courier New',monospace", fontSize:'1.1rem', fontWeight:'800', color:netProfit>=0?'#166534':'#991b1b', borderTop:'3px double', borderBottom:'3px double', padding:'2px 10px' }}>
            {fmtNumRaw(Math.abs(netProfit))}
          </span>
        </div>

      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const FinancialReports = () => {
  const today      = new Date().toISOString().slice(0,10);
  const monthStart = today.slice(0,8) + '01';

  const [reportType, setReportType] = useState('daybook');
  const [startDate,  setStartDate]  = useState(monthStart);
  const [endDate,    setEndDate]    = useState(today);
  const [acheadId,   setAcheadId]   = useState('');
  const [fyearId,    setFyearId]    = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [heads,    setHeads]    = useState([]);
  const [years,    setYears]    = useState([]);
  const [company,  setCompany]  = useState({});
  const [rows,     setRows]     = useState([]);
  const [summary,  setSummary]  = useState({});
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [hasLoaded,setHasLoaded]= useState(false);

  // Load dropdowns
  useEffect(() => {
    apiClient.get('/FinancialReports.php?action=dropdowns').then(res => {
      if (res.data.success) {
        const d = res.data.data;
        setHeads(d.heads || []);
        setYears(d.years || []);
        setCompany(d.company || {});
        const cur = (d.years || []).find(y => y.iscurrent === '1' || y.iscurrent === 1);
        if (cur) {
          setFyearId(String(cur.yearid));
          setStartDate(cur.startdate || monthStart);
          setEndDate(cur.enddate < today ? cur.enddate : today);
        }
      }
    }).catch(e => console.error('Dropdown error:', e));
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true); setError(''); setHasLoaded(false);
    const params = new URLSearchParams({
      report_type: reportType,
      start_date:  startDate,
      end_date:    endDate,
      achead_id:   acheadId || '0',
      fyear_id:    fyearId  || '0',
    });
    try {
      const res = await apiClient.get(`/FinancialReports.php?${params}`);
      if (res.data.success) {
        setRows(res.data.data.rows || []);
        setSummary(res.data.data.summary || {});
        setHasLoaded(true);
      } else {
        setError(res.data.message || 'Failed to load report');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error loading report');
    } finally { setLoading(false); }
  }, [reportType, startDate, endDate, acheadId, fyearId]);

  const exportCSV = () => {
    if (!rows.length) return;
    const keys   = Object.keys(rows[0]);
    const header = keys.join(',');
    const body   = rows.map(r => keys.map(k => `"${String(r[k]??'').replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([header+'\n'+body], { type: 'text/csv' }));
    a.download = `${reportType}_${startDate}_${endDate}.csv`;
    a.click();
  };

  const handlePrint = () => {
    const rt = REPORT_TYPES.find(r => r.key === reportType);
    const win = window.open('','_blank');
    // Print CSS — just re-render from rows for print
    win.document.write(`<!DOCTYPE html><html><head><title>${rt?.label} — ${company.companyname||''}</title>
    <style>
      body { font-family: Georgia, serif; font-size: 11px; color: #1f2937; margin: 12mm; }
      h1 { font-size: 16px; margin: 0; } h2 { font-size: 12px; font-weight: normal; color: #6b7280; margin: 2px 0 8px; }
      table { border-collapse: collapse; width: 100%; margin-top: 8px; }
      th { background: #1e293b; color: #fff; padding: 5px 8px; text-align: left; font-size: 10px; }
      td { padding: 4px 8px; border-bottom: 1px solid #e5e7eb; font-size: 10px; }
      tr:nth-child(even) td { background: #f9fafb; }
      .right { text-align: right; } .mono { font-family: monospace; }
      @media print { @page { margin: 10mm; } }
    </style></head><body>
    <h1>${company.companyname||'Financial Report'}</h1>
    <h2>${rt?.label} &nbsp;|&nbsp; ${startDate} to ${endDate}</h2>
    <table><thead><tr>${Object.keys(rows[0]||{}).map(k=>`<th>${k}</th>`).join('')}</tr></thead>
    <tbody>${rows.map(r=>`<tr>${Object.values(r).map(v=>`<td>${v??''}</td>`).join('')}</tr>`).join('')}</tbody></table>
    <script>window.onload=()=>{window.print();}</script></body></html>`);
    win.document.close();
  };

  const rt = REPORT_TYPES.find(r => r.key === reportType);
  const showAcHead = reportType === 'ledger';
  const isStatementReport = ['balance_sheet','profit_loss','trading_pl'].includes(reportType);

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', background:'#f8fafc', fontFamily:"'Libre Baskerville', Georgia, serif" }}>

      {/* FONT IMPORT */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Courier+Prime:ital,wght@0,400;0,700;1,400&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
      `}</style>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        padding: '20px 28px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '3px solid #334155', flexShrink: 0,
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:18 }}>
          <div style={{ width:50, height:50, background:'rgba(255,255,255,0.08)', borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid rgba(255,255,255,0.12)' }}>
            <rt.icon size={24} color={rt.color} />
          </div>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ color:'#94a3b8', fontSize:'0.72rem', textTransform:'uppercase', letterSpacing:'0.12em', fontFamily:"'Libre Baskerville',Georgia,serif" }}>
                Financial Reports
              </span>
              <span style={{ color:'#475569', fontSize:'0.7rem' }}>›</span>
              <span style={{ color:rt.color, fontSize:'0.72rem', textTransform:'uppercase', letterSpacing:'0.1em' }}>{rt.label}</span>
            </div>
            <div style={{ fontFamily:"'DM Serif Display',Georgia,serif", color:'#f1f5f9', fontSize:'1.5rem', fontWeight:'400', marginTop:2 }}>
              {company.companyname || 'Financial Statements'}
            </div>
          </div>
        </div>
        {hasLoaded && (
          <div style={{ display:'flex', gap:24, animation:'fadeIn .4s ease' }}>
            {summary.total_debit != null && (
              <Stat label="Total Debit"  value={fmtNumRaw(summary.total_debit)}  color="#fca5a5" />
            )}
            {summary.total_credit != null && (
              <Stat label="Total Credit" value={fmtNumRaw(summary.total_credit)} color="#86efac" />
            )}
            {summary.net_profit != null && (
              <Stat label={summary.net_profit >= 0 ? 'Net Profit' : 'Net Loss'} value={fmtNumRaw(Math.abs(summary.net_profit))} color={summary.net_profit >= 0 ? '#6ee7b7' : '#fca5a5'} />
            )}
            {summary.total_assets != null && (
              <Stat label="Total Assets" value={fmtNumRaw(summary.total_assets)} color="#93c5fd" />
            )}
            <Stat label="Records" value={summary.total_rows} color="#e2e8f0" />
          </div>
        )}
      </div>

      {/* ── Report Type Tabs ───────────────────────────────────────────────── */}
      <div style={{ background:'#1e293b', padding:'10px 20px', display:'flex', gap:4, overflowX:'auto', flexShrink:0, borderBottom:'1px solid #334155' }}>
        {REPORT_TYPES.map(r => {
          const active = r.key === reportType;
          const Icon   = r.icon;
          return (
            <button key={r.key}
              onClick={() => { setReportType(r.key); setHasLoaded(false); }}
              style={{
                display:'flex', alignItems:'center', gap:6, padding:'7px 14px',
                borderRadius:7, border:`1px solid ${active ? r.color+'80' : 'transparent'}`,
                cursor:'pointer', whiteSpace:'nowrap', fontSize:'0.8rem',
                fontFamily:"'Libre Baskerville',Georgia,serif",
                fontWeight: active ? '700' : '400',
                background: active ? `${r.color}22` : 'transparent',
                color: active ? r.color : '#94a3b8',
                transition: 'all .15s',
              }}
            >
              <Icon size={13} />{r.label}
            </button>
          );
        })}
      </div>

      {/* ── Filters ───────────────────────────────────────────────────────── */}
      <div style={{ padding:'12px 20px', background:'#fff', borderBottom:'1px solid #e2e8f0', flexShrink:0, boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ display:'grid', gridTemplateColumns:`repeat(auto-fit,minmax(150px,1fr))`, gap:10, alignItems:'end' }}>
          <div>
            <label style={lblStyle}>Financial Year</label>
            <select value={fyearId} onChange={e => setFyearId(e.target.value)} style={inpStyle}>
              <option value="">— Current Year —</option>
              {years.map(y => (
                <option key={y.yearid} value={y.yearid}>{y.yearname}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={lblStyle}>{isStatementReport ? 'As at Date' : 'From Date'}</label>
            {!isStatementReport && <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} style={inpStyle} />}
            {isStatementReport && <input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} style={inpStyle} />}
          </div>
          {!isStatementReport && (
            <div>
              <label style={lblStyle}>To Date</label>
              <input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} style={inpStyle} />
            </div>
          )}
          {showAcHead && (
            <div>
              <label style={lblStyle}>Account Head</label>
              <select value={acheadId} onChange={e=>setAcheadId(e.target.value)} style={inpStyle}>
                <option value="">All Accounts</option>
                {heads.map(h => (
                  <option key={h.acheadid} value={h.acheadid}>
                    {h.acheadcode} — {h.acheadname}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label style={lblStyle}>&nbsp;</label>
            <button onClick={loadReport} disabled={loading} style={{
              width:'100%', padding:'9px 14px', borderRadius:7, border:'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              background: loading ? '#94a3b8' : `linear-gradient(135deg, ${rt.color}, ${rt.color}cc)`,
              color:'#fff', fontWeight:'700', fontSize:'0.88rem',
              fontFamily:"'Libre Baskerville',Georgia,serif",
              display:'flex', alignItems:'center', justifyContent:'center', gap:6,
              boxShadow: loading ? 'none' : `0 2px 8px ${rt.color}40`,
              transition:'all .15s',
            }}>
              {loading ? <><Spinner />Generating…</> : <><RefreshCw size={14} />Generate Report</>}
            </button>
          </div>
        </div>
      </div>

      {/* ── Toolbar ───────────────────────────────────────────────────────── */}
      {hasLoaded && (
        <div style={{ padding:'8px 20px', background:'#f8fafc', borderBottom:'1px solid #e2e8f0', display:'flex', alignItems:'center', gap:10, flexShrink:0, animation:'fadeIn .3s ease' }}>
          <span style={{ fontSize:'0.78rem', color:'#64748b' }}>
            <Calendar size={12} style={{ display:'inline', marginRight:4 }} />
            {isStatementReport ? `As at ${fmtDate(endDate)}` : `${fmtDate(startDate)} — ${fmtDate(endDate)}`}
          </span>
          <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
            {['daybook','ledger','trial_balance'].includes(reportType) && (
              <div style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', border:'1px solid #e2e8f0', borderRadius:7, background:'#fff' }}>
                <Search size={13} color="#94a3b8" />
                <input type="text" placeholder="Search…" value={searchTerm}
                  onChange={e=>setSearchTerm(e.target.value)}
                  style={{ border:'none', outline:'none', width:130, fontSize:'0.82rem', fontFamily:"'Libre Baskerville',Georgia,serif" }} />
              </div>
            )}
            <button onClick={exportCSV} style={btnStyle}><Download size={14} /> Export CSV</button>
            <button onClick={handlePrint} style={btnStyle}><Printer size={14} /> Print</button>
          </div>
        </div>
      )}

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {error && (
        <div style={{ margin:'12px 20px', padding:'10px 14px', borderRadius:8, background:'#fef2f2', border:'1px solid #fca5a5', color:'#dc2626', fontSize:'0.88rem', flexShrink:0 }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* ── Content ───────────────────────────────────────────────────────── */}
      <div style={{ flex:1, overflow:'auto', padding:'16px 20px' }}>
        {!hasLoaded && !loading && (
          <div style={{ textAlign:'center', padding:'80px 20px', color:'#94a3b8', animation:'fadeIn .5s ease' }}>
            <rt.icon size={52} style={{ margin:'0 auto 16px', opacity:.2, display:'block' }} />
            <p style={{ fontFamily:"'DM Serif Display',Georgia,serif", fontSize:'1.2rem', margin:'0 0 6px', color:'#64748b' }}>
              {rt.label}
            </p>
            <p style={{ margin:0, fontSize:'0.85rem' }}>Configure filters above and click <strong>Generate Report</strong></p>
          </div>
        )}

        {loading && (
          <div style={{ textAlign:'center', padding:'80px', color:rt.color }}>
            <div style={{ width:36, height:36, border:`3px solid ${rt.color}30`, borderTopColor:rt.color, borderRadius:'50%', animation:'spin .7s linear infinite', margin:'0 auto 16px' }} />
            <p style={{ fontFamily:"'DM Serif Display',Georgia,serif", fontSize:'1rem', margin:0 }}>Preparing {rt.label}…</p>
          </div>
        )}

        {hasLoaded && !loading && rows.length === 0 && (
          <div style={{ textAlign:'center', padding:'60px', color:'#94a3b8' }}>
            <BookOpen size={40} style={{ margin:'0 auto 10px', opacity:.3, display:'block' }} />
            <p style={{ margin:0 }}>No data found for the selected period.</p>
          </div>
        )}

        {hasLoaded && !loading && rows.length > 0 && (
          <div style={{ animation:'fadeIn .4s ease' }}>
            {/* Company header for statement reports */}
            {isStatementReport && company.companyname && (
              <div style={{ textAlign:'center', marginBottom:20, padding:'16px', border:'1px solid #e2e8f0', borderRadius:8, background:'#fff' }}>
                <div style={{ fontFamily:"'DM Serif Display',Georgia,serif", fontSize:'1.2rem', color:'#1e293b', fontWeight:'700' }}>{company.companyname}</div>
                {company.address && <div style={{ color:'#64748b', fontSize:'0.8rem', marginTop:2 }}>{company.address}{company.city ? ', ' + company.city : ''}</div>}
                <div style={{ color:rt.color, fontSize:'0.82rem', fontWeight:'700', marginTop:6, textTransform:'uppercase', letterSpacing:'0.08em' }}>
                  {rt.label} — {isStatementReport ? `As at ${fmtDate(endDate)}` : `For the period ${fmtDate(startDate)} to ${fmtDate(endDate)}`}
                </div>
              </div>
            )}

            {reportType === 'daybook'       && <DaybookTable      rows={rows} searchTerm={searchTerm} />}
            {reportType === 'ledger'        && <LedgerTable       rows={rows} searchTerm={searchTerm} />}
            {reportType === 'trial_balance' && <TrialBalanceTable rows={rows} />}
            {reportType === 'balance_sheet' && <BalanceSheetTable rows={rows} summary={summary} />}
            {reportType === 'profit_loss'   && <ProfitLossTable   rows={rows} summary={summary} />}
            {reportType === 'trading_pl'    && <TradingPLTable    rows={rows} summary={summary} />}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared styles
// ─────────────────────────────────────────────────────────────────────────────
const tableStyle = {
  width:'100%', borderCollapse:'collapse', fontSize:'0.83rem',
  background:'#fff', borderRadius:8, overflow:'hidden',
  boxShadow:'0 1px 4px rgba(0,0,0,0.06)',
};
const thStyle = {
  padding:'9px 12px', fontWeight:'700', fontSize:'0.72rem',
  color:'#94a3b8', letterSpacing:'0.06em', textTransform:'uppercase',
  background:'#1e293b', whiteSpace:'nowrap',
  fontFamily:"'Libre Baskerville',Georgia,serif",
};
const tdStyle = {
  padding:'7px 12px', borderBottom:'1px solid #f1f5f9',
  verticalAlign:'middle', fontFamily:"'Libre Baskerville',Georgia,serif",
};
const lblStyle = {
  display:'block', fontSize:'0.74rem', fontWeight:'700',
  color:'#374151', marginBottom:4, letterSpacing:'0.03em',
  fontFamily:"'Libre Baskerville',Georgia,serif",
};
const inpStyle = {
  width:'100%', padding:'8px 10px', border:'1px solid #d1d5db',
  borderRadius:6, fontSize:'0.83rem', background:'#fff',
  boxSizing:'border-box', fontFamily:"'Libre Baskerville',Georgia,serif",
};
const btnStyle = {
  display:'flex', alignItems:'center', gap:5, padding:'7px 12px',
  border:'1px solid #e2e8f0', borderRadius:7, background:'#fff',
  color:'#374151', cursor:'pointer', fontSize:'0.8rem',
  fontFamily:"'Libre Baskerville',Georgia,serif", fontWeight:'600',
};

const Spinner = () => (
  <div style={{ width:14, height:14, border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin .7s linear infinite', display:'inline-block' }} />
);

const Stat = ({ label, value, color }) => (
  <div style={{ textAlign:'right' }}>
    <div style={{ fontSize:'0.66rem', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'Libre Baskerville',Georgia,serif" }}>{label}</div>
    <div style={{ fontFamily:"'Courier Prime','Courier New',monospace", color, fontWeight:'700', fontSize:'0.9rem' }}>{value}</div>
  </div>
);

export default FinancialReports;